import { NodeConfig, TreeGraph, TreeGraphData } from '@antv/g6';
import {
  CallChaining,
  FormFieldWidget,
  ILevel,
  ModelFieldType,
  SPI,
  useMessageHub,
  ValidatorInfo,
  ValidatorStatus,
  ViewType,
  Widget
} from '@kunlun/dependencies';
import { cloneDeep, debounce, isNil } from 'lodash-es';
import MindMap from './MindMap.vue';
import { actionBarHeight, conditionRowHeight } from './nodes';
import {
  createActivityTypeData,
  createConditionData,
  createJsonData,
  createPolicyData,
  createRightsInfoData,
  createSimpleConditionData,
  rootNodeData
} from './templateData';
import { queryPromotionType } from './services';

@SPI.ClassFactory(
  FormFieldWidget.Token({
    viewType: ViewType.Form,
    ttype: ModelFieldType.ManyToOne,
    widget: 'MindMap',
    multi: false
  })
)
export class FormM2oMindMapFieldWidget extends FormFieldWidget {
  public initialize(props) {
    super.initialize(props);
    this.setComponent(MindMap);
    window.addEventListener('message', this.msgHandler);

    return this;
  }
  @Widget.Reactive()
  public baseData: {
    promotionTypeEnumList: null | { name: string; value: string }[];
  } = {
    promotionTypeEnumList: null
  };

  treeGraph: TreeGraph | undefined;

  // 更新推广方案标题
  updatePlanTitle = debounce((title) => {
    if (this.treeGraph) {
      const data = this.treeGraph.getNodes()[0].getModel();
      this.rowData[data!.dataId as string].name = title;
      this.treeGraph.updateItem(this.treeGraph.getNodes()[0], data);
    }
  }, 1500);

  @Widget.Reactive()
  get merchBill() {
    return this.formData.merchBill;
  }

  @Widget.Reactive()
  treeData: any = {};

  // 扁平化业务数据，{ [id]: nodeJson }
  @Widget.Reactive()
  rowData: any = {};

  @Widget.Reactive()
  _formData: any = {};

  @Widget.Reactive()
  @Widget.Inject()
  protected mountedCallChaining: CallChaining | undefined;

  private jsonTpl = createJsonData(1, 1, 1);

  // 更新推广方案标题
  @Widget.Watch('formData.name', { immediate: true })
  watchPlanName(value) {
    if (value) {
      this.updatePlanTitle(value);
    }
  }

  @Widget.Watch('formData.promotionType', { immediate: true })
  watchPromotionType(val) {
    this.treeData = undefined;
    // 编辑时，初始化树结构
    if (val === 'GROUP_MARKETING') {
      this.parsePromotionDesignJson(() => {
        window.postMessage('init-tree-graph');
      });
    }
  }

  @Widget.Watch('formData', { deep: true })
  watchAccountTimeRule(val) {
    this._formData = val;
  }
  protected async mounted() {
    try {
      super.mounted();
      const res = await queryPromotionType(this.model);
      this.baseData.promotionTypeEnumList = res?.promotionTypeEnumList?.map(item => { return { label:item.displayName,value:item.value}})
    } catch (error) {
      console.error(error)
    }
  }

  protected unmounted(): void {
    super.unmounted();
    this.treeGraph?.destroy();
    this.treeGraph = undefined;
    window.removeEventListener('message', this.msgHandler);
  }

  private parsePromotionDesignJson(cb?: () => void) {
    if (this.formData.promotionDesignJson) {
      console.time('逆向解析树数据');
      const json = JSON.parse(this.formData.promotionDesignJson as string);
      json.id += '';

      let rowData: any = {};
      let treeData: any = rootNodeData({ jData: json, rowData });

      // 第一层
      const copyJson = cloneDeep(json);
      // 有前置条件
      if (copyJson.hasPrecondition) {
        const copy = cloneDeep(copyJson);
        copy.children = [];

        treeData.actionBar.switchAction.open = true;

        const conditionNode = createConditionData({
          isPre: true,
          jsonData: copy,
          isEdit: true,
          rowData
        }) as NodeConfig & Record<string, any>;
        Array(copy.conditions.length - 1)
          .fill('')
          .forEach((i) => {
            conditionNode.y! -= conditionRowHeight / 2;
            conditionNode.size![1] += conditionRowHeight;
            conditionNode.anchorPoints = [
              [0, (conditionNode.size![1] - actionBarHeight) / 2 / conditionNode.size![1]],
              [1, (conditionNode.size![1] - actionBarHeight) / 2 / conditionNode.size![1]]
            ];
          });
        // @ts-ignore
        treeData.children = [conditionNode];
        this.parseTreeData(copyJson, treeData.children[0], rowData, 2);
      } else {
        this.parseTreeData(copyJson, treeData, rowData, 2);
      }

      this.treeData = treeData;
      Object.assign(this.rowData, rowData);
      console.timeEnd('逆向解析树数据');
    }
    cb && cb();
  }

  // 逆向解析树结构
  parseTreeData(json, node, rowData, level?: number, policyId?: string) {
    const copyJson = cloneDeep(json);

    copyJson.children.forEach((i, index) => {
      let child: TreeGraphData;
      i.id += '';
      i.parentId += '';

      // 第二层
      if (level === 2) {
        let policyId = i.id;
        i.policyInfo.name = i.name;
        if (!isNil(i.policyInfo.accountTimeRule)) {
          if (!isNil(i.policyInfo.paymentDays)) {
            i.policyInfo.paymentDays += '';
          } else {
            i.policyInfo.paymentDays = '';
          }
        } else {
          i.policyInfo.accountTimeRule = '';
          i.policyInfo.paymentDays = '';
          i.policyInfo.accountDate = '';
        }
        child = createPolicyData({ parentId: i.parentId, jData: i, rowData });

        // 前置条件
        if (i.hasPrecondition) {
          const copy = cloneDeep(i);
          copy.children = [];
          (child.actionBar as any).switchAction.open = true;
          const conditionNode = createConditionData({
            isPre: true,
            jsonData: copy,
            isEdit: true,
            rowData
          }) as NodeConfig & Record<string, any>;
          Array(copy.conditions.length - 1)
            .fill('')
            .forEach((i) => {
              conditionNode.y! -= conditionRowHeight / 2;
              conditionNode.size![1] += conditionRowHeight;
              conditionNode.anchorPoints = [
                [0, (conditionNode.size![1] - actionBarHeight) / 2 / conditionNode.size![1]],
                [1, (conditionNode.size![1] - actionBarHeight) / 2 / conditionNode.size![1]]
              ];
            });
          // @ts-ignore
          child.children = [conditionNode];
          node.children.push(child);
          this.parseTreeData(i, node.children[index].children[0], rowData, 3, policyId);
        } else {
          node.children.push(child);
          this.parseTreeData(i, node.children[index], rowData, 3, policyId);
        }
      }
      // 第三层
      else if (level === 3) {
        const copy = cloneDeep(i);
        copy.children = [];

        // 活动类型
        child = createActivityTypeData({ parentId: i.parentId, jData: copy, rowData });

        // 前置条件
        if (i.hasPrecondition) {
          (child.actionBar as any).switchAction.open = true;
          const conditionNode = createConditionData({
            isPre: true,
            jsonData: copy,
            isEdit: true,
            rowData
          }) as NodeConfig & Record<string, any>;
          Array(copy.conditions.length - 1)
            .fill('')
            .forEach((i) => {
              conditionNode.y! -= conditionRowHeight / 2;
              conditionNode.size![1] += conditionRowHeight;
              conditionNode.anchorPoints = [
                [0, (conditionNode.size![1] - actionBarHeight) / 2 / conditionNode.size![1]],
                [1, (conditionNode.size![1] - actionBarHeight) / 2 / conditionNode.size![1]]
              ];
            });
          // @ts-ignore
          child.children = [conditionNode];

          // 门槛规则
          let ruleConditionNode;
          if (copy.optionType !== 'DIRECT_DESCENT') {
            ruleConditionNode = createConditionData({
              isPre: false,
              jsonData: copy,
              isEdit: true,
              rowData
            }) as NodeConfig & Record<string, any>;
            Array(copy.effectConditions.length - 1)
              .fill('')
              .forEach((i) => {
                ruleConditionNode.y! -= conditionRowHeight / 2;
                ruleConditionNode.size![1] += conditionRowHeight;
                ruleConditionNode.anchorPoints = [
                  [0, (ruleConditionNode.size![1] - actionBarHeight) / 2 / ruleConditionNode.size![1]],
                  [1, (ruleConditionNode.size![1] - actionBarHeight) / 2 / ruleConditionNode.size![1]]
                ];
              });
          } else {
            ruleConditionNode = createSimpleConditionData({ jsonData: copy, rowData }) as NodeConfig &
              Record<string, any>;
          }

          // @ts-ignore
          child.children![0].children = [ruleConditionNode];
          // 权益信息
          const rightsInfoNode = createRightsInfoData({ jsonData: copy, rowData });
          rightsInfoNode.policyId = policyId || '';
          rightsInfoNode.activityType = copy.optionType;
          if (['REDUCTION_AMOUNT', 'DISCOUNT_RATE'].includes(copy.equityInformationRule.reductionOption)) {
            rightsInfoNode.actionBar.linkAction = false;
            rightsInfoNode.actionBar.settingAction = true;
          }
          // 满赠
          if (copy.optionType === 'FULL_GWP') {
            // 权益类型为"料号满A赠A"或"货号满A赠A"
            if (['ITEM_FULL_A_TO_A', 'ARTICLE_FULL_A_TO_A'].includes(copy.equityInformationRule.giftOption)) {
              rightsInfoNode.actionBar.linkAction = false;
            } else {
              rightsInfoNode.actionBar.linkAction = true;
            }
          } else {
            rightsInfoNode.actionBar.linkAction = false;
          }
          child.children![0].children![0].children = [rightsInfoNode];
        } else {
          // 门槛规则
          let ruleConditionNode;
          if (copy.optionType !== 'DIRECT_DESCENT') {
            ruleConditionNode = createConditionData({
              isPre: false,
              jsonData: copy,
              isEdit: true,
              rowData
            }) as NodeConfig & Record<string, any>;
            Array(copy.effectConditions.length - 1)
              .fill('')
              .forEach((i) => {
                ruleConditionNode.y! -= conditionRowHeight / 2;
                ruleConditionNode.size![1] += conditionRowHeight;
                ruleConditionNode.anchorPoints = [
                  [0, (ruleConditionNode.size![1] - actionBarHeight) / 2 / ruleConditionNode.size![1]],
                  [1, (ruleConditionNode.size![1] - actionBarHeight) / 2 / ruleConditionNode.size![1]]
                ];
              });
          } else {
            ruleConditionNode = createSimpleConditionData({ jsonData: copy, rowData }) as NodeConfig &
              Record<string, any>;
          }
          // @ts-ignore
          child.children = [ruleConditionNode];
          // 权益信息
          const rightsInfoNode = createRightsInfoData({ jsonData: copy, rowData });
          rightsInfoNode.policyId = policyId || '';
          rightsInfoNode.activityType = copy.optionType;
          if (['REDUCTION_AMOUNT', 'DISCOUNT_RATE'].includes(copy.equityInformationRule.reductionOption)) {
            rightsInfoNode.actionBar.linkAction = false;
            rightsInfoNode.actionBar.settingAction = true;
          }
          // 满赠
          if (copy.optionType === 'FULL_GWP') {
            // 权益类型为"料号满A赠A"或"货号满A赠A"
            if (['ITEM_FULL_A_TO_A', 'ARTICLE_FULL_A_TO_A'].includes(copy.equityInformationRule.giftOption)) {
              rightsInfoNode.actionBar.linkAction = false;
            } else {
              rightsInfoNode.actionBar.linkAction = true;
            }
          } else {
            rightsInfoNode.actionBar.linkAction = false;
          }
          child.children![0].children = [rightsInfoNode];
        }

        node.children.push(child);
      }
    });
  }

  @Widget.Method()
  setTreeGraph(treeGraph: TreeGraph) {
    this.treeGraph = treeGraph;
    if (this.treeGraph) {
      const data = this.treeGraph.getNodes()[0].getModel();
      this.rowData[data!.id as string].name = this.formData.name || '推广方案名称';
      this.treeGraph.layout();
    }
  }

  public async validator(): Promise<ValidatorInfo> {
    try {
      const data = this.submitTree(true);
      this.formData.promotionDesignJson = JSON.stringify(data);
    } catch (error) {
      console.error(error);
    }

    return {
      message: '',
      status: ValidatorStatus.Success,
      path: ''
    };
  }

  removeUnusedAttrs(data, tpl) {
    for (const key in data) {
      if (!Object.prototype.hasOwnProperty.call(tpl, key)) {
        delete data[key];
      }
      // 对象
      else if (Object.prototype.toString.call(tpl[key]) === '[object Object]') {
        this.removeUnusedAttrs(data[key], tpl[key]);
      }
      // 数组
      else if (Array.isArray(tpl[key]) && tpl[key].length) {
        data[key].forEach((i) => {
          this.removeUnusedAttrs(i, tpl[key][0]);
        });
      }
    }
    data?.children?.forEach((child) => {
      this.removeUnusedAttrs(child, this.jsonTpl);
    });
  }

  collectData(allData, res, isSubmit = false) {
    allData.forEach((i, index) => {
      // 清空无用数据
      if (i.level !== 3) {
        i.equityInformationRule = {
          name: '',
          directDescentOption: '', // 直降门槛规则
          reductionOption: '', //活动类型=满减时的优惠操作
          giftOption: '', //活动类型=满赠时的优惠操作
          reductionDiscount: '', //操作类型为满减时的优惠类型
          giftDiscount: '', //操作为满赠时的优惠类型
          discountValue: 0, //优惠数值
          giftItemStr: '', // 满赠清单
          equityInfoItemStr: '', // 权益信息清单
          realPriceNotBenefits: true, // 实价产品不让利
          buyItNotBenefits: true, // 一口价产品不让利
          isAdvanced: false, // 是否高级配置
          advanceItemStr: '' // 高级配置清单
        };
      }
      // 寻找子节点
      if (i.parentId === res.id && !res.children.map((a) => a.id).includes(i.id)) {
        res.children.push(i);
      }
      if (index === allData.length - 1) {
        res.children?.forEach((b) => {
          this.collectData(allData, b, isSubmit);
        });
      }
    });
  }

  typeMap = {
    and: '&',
    or: '|'
  };

  // 生成表达式
  genExpression(res) {
    let conditionExppression = '';
    let effectConditionExppression = '';

    conditionExppression = (res.conditionsCombine.groups as any[]).reduce((t, c, index) => {
      const innerStr = c._uniqueIds.reduce((a, b, i) => {
        return a + b + (i === c._uniqueIds.length - 1 ? '' : this.typeMap[c.type]);
      }, '');
      const outStr =
        t +
        `(${innerStr})` +
        (index === res.conditionsCombine.groups.length - 1 ? '' : this.typeMap[res.conditionsCombine.type]);
      return outStr;
    }, '');

    effectConditionExppression = (res.effectConditionsCombine.groups as any[]).reduce((t, c, index) => {
      const innerStr = c._uniqueIds.reduce((a, b, i) => {
        return a + b + (i === c._uniqueIds.length - 1 ? '' : this.typeMap[c.type]);
      }, '');
      const outStr =
        t +
        `(${innerStr})` +
        (index === res.effectConditionsCombine.groups.length - 1 ? '' : this.typeMap[res.effectConditionsCombine.type]);
      return outStr;
    }, '');

    res.logicalExpression = conditionExppression;
    res.effectLogicalExpression = effectConditionExppression;

    res.children?.forEach((i) => {
      this.genExpression(i);
    });
  }

  @Widget.Reactive()
  submitTree(isSubmit = false) {
    if (!this.treeGraph) return;
    const allData = this.treeGraph.getNodes().map((i) => this.rowData[i.getModel().dataId as string]);
    allData.forEach((i) => ((i as any).children = []));
    // 去重
    let uniq: any = [];
    allData.forEach((el) => {
      if (!uniq.some((e) => e.id == (el as any).id)) {
        uniq.push(el);
      }
    });

    const res = uniq[0];
    this.collectData(uniq, res, isSubmit);
    this.genExpression(res);

    return res;
  }

  private updateTree(treeData?: any) {
    const oldTranslate = this.treeGraph!.get('group').getMatrix();
    this.treeGraph!.changeData(treeData || this.treeGraph!.getNodes()[0].getModel());
    this.treeGraph!.get('group').setMatrix(oldTranslate);
    window.postMessage('ActivityFillData');
  }

  private msgHandler = (e) => {
    if (e.data === 'M2MTableDraftSaveData') {
      try {
        const data = this.submitTree();
        this.formData.promotionDesignJson = JSON.stringify(data);
      } catch (error) {
        console.error(error);
      }
    } else if (e.data === 'M2MTableDraftFillData') {
      const timer = setTimeout(() => {
        clearTimeout(timer);
        this.parsePromotionDesignJson();
        this.updateTree(this.treeData);
      }, 1500);
    }
  };
}
